import { axiosInstance } from "@/lib/axiosInstance";

/**
 * Offers live on the recruiter service (:8013) and are returned as plain
 * objects — there is no { success, data } envelope here.
 *
 * Extending mirrors the row into the student app, sends the "You Have an Offer"
 * email and moves the application to `offer_extended`. No extra call is needed.
 */

/**
 * ⚠️ The two services use DIFFERENT names for the initial state:
 *   recruiter API → "extended"   (what we read here)
 *   student API   → "pending"
 * Never copy the student value into this file; every offer we fetch starts as
 * `extended`.
 */
export type OfferStatusType =
  | "extended"
  | "accepted"
  | "declined"
  | "expired"
  | "revoked";

export type OfferExtendType = {
  stipend?: number | null;
  start_date?: string | null; // YYYY-MM-DD
  end_date?: string | null; // YYYY-MM-DD
  offer_letter_url?: string | null;
  expires_at?: string | null; // ISO-8601 datetime
};

export type OfferType = {
  id: string;
  application_id: string;
  stipend: number | null;
  start_date: string | null;
  end_date: string | null;
  offer_letter_url: string | null;
  status: OfferStatusType;
  student_response_at: string | null;
  expires_at: string | null;
  created_at: string;
};

export type OfferListType = {
  items: OfferType[];
  total: number;
  page: number;
  limit: number;
};

/**
 * POST /v1/offers?application_id=… — application_id goes in the query string,
 * not the body.
 *
 * An application holds at most one live offer. Extending is an upsert: a
 * `revoked` or `expired` offer on the same application is overwritten, so a
 * revoke → re-extend correction works. An `extended` or `accepted` offer is
 * not, and returns 409.
 *
 * Rejects with 409 in two cases:
 *   OFFER_ALREADY_EXISTS          — a live offer is already out
 *   OFFER_NOT_ALLOWED_FOR_STATUS  — only shortlisted / interview_scheduled /
 *                                   interviewed applications may receive one
 */
export const extendOffer = async (
  applicationId: string,
  data: OfferExtendType
): Promise<OfferType> => {
  const response = await axiosInstance.post("/v1/offers", data, {
    params: { application_id: applicationId },
  });
  return response.data;
};

/**
 * There is no per-application filter on this endpoint, so callers that need
 * one offer have to pull a page and match on `application_id` themselves.
 */
export const fetchOffers = async (params?: {
  page?: number;
  limit?: number;
}): Promise<OfferListType> => {
  const response = await axiosInstance.get("/v1/offers", { params });
  return response.data;
};

/**
 * PATCH /v1/offers/{id}/revoke — the correction path for a wrong stipend.
 *
 * Sets the offer to `revoked`, returns the application to whatever status it
 * held before the offer (recorded server-side, not guessed), syncs to the
 * student and emails them. A corrected offer can then be extended again.
 *
 * Only possible while the offer is still `extended`. Once the student has
 * accepted or declined, revoking fails with INVALID_STATE_TRANSITION — their
 * answer is final.
 */
export const revokeOffer = async (offerId: string): Promise<OfferType> => {
  const response = await axiosInstance.patch(`/v1/offers/${offerId}/revoke`);
  return response.data;
};
